import React, { useRef, useEffect, useCallback, useState } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import _throttle from 'lodash/throttle';

import { myProfile } from 'Redux/user-profile/selectors';
import { Box } from 'Components/Base';
import getConfig from 'Util/Config';
import sanitizeVideoTime from 'Util/videoProgress';
import { useMindstampVideoPlayer } from 'Context/MindstampVideoPlayer';
import { VIDEO_STATE } from 'Context/constant';

import { VIDEO_PROVIDER } from './constant';

const config = getConfig();
const { resumeVideo, completedPercentage } = config.videoPlayer;
const { classroomTimeInterval } = config;

const UPDATE_VIDEO_FACTOR = resumeVideo.updateFactor;
const THROTTLE_UPDATE_VIDEO_TIME = 15 * UPDATE_VIDEO_FACTOR * 1000;
const RESUME_OFFSET_SEC = resumeVideo.resumeOffsetSec;
const THROTTLE_VIDEO_PROGRESS_MILLISEC = 1000;
const VIDEO_COMPLETE_PERCENTAGE = completedPercentage;
const THROTTLE_UPDATE_CLASSROOM_TIME_INTERVAL =
  classroomTimeInterval > 5000 ? classroomTimeInterval : 5000;

/**
 * generate URL for setting Mindstamp player and report
 * @param {string} baseUrl
 * @param {User} user
 * @param {number} latestVideoTime
 * @param {boolean} isResuming
 * @returns mindstamp URL
 */
function generateMindstampUrl(baseUrl, user, latestVideoTime, isResuming) {
  if (!user) return '';

  const mindstampUrl = new URL(baseUrl);

  // set user info for Mindstamp report
  mindstampUrl.searchParams.set('custom_id', user.additional6);
  mindstampUrl.searchParams.set('name', user.firstname);

  // set resume time
  if (isResuming && latestVideoTime > 0) {
    const resumeTime = Math.max(latestVideoTime - RESUME_OFFSET_SEC, 0);
    mindstampUrl.searchParams.set('t', resumeTime);
  }

  return mindstampUrl;
}

/**
 * @typedef MindstampVideoBoxProps
 * @property {{url: string, duration: number}} value
 * @property {string} title
 * @property {boolean} isResuming
 * @property {{id: string, videoTime: number}} videoProgress
 * @property {(time: number) => void} onUpdateVideoTime
 * @property {() => void} onCompleted
 * @property {(watchedDuration: number) => void} onUpdateClassroomTime
 * @property {() => void} checkInvalidDevice
 * @property {User} user
 *
 * @param {MindstampVideoBoxProps} props
 */
const MindstampVideoBox = ({
  value,
  title,
  isResuming,
  videoProgress,
  onUpdateVideoTime,
  onCompleted,
  onUpdateClassroomTime,
  checkInvalidDevice,
  user,
  showVideoOverlayOnEnd,
  renderCustomVideoOverlay,
}) => {
  const { url: videoUrl, duration: videoDuration } = value;
  const previousTime = useRef(null);
  const isSetCompleted = useRef(false);
  const completedTime = useRef((VIDEO_COMPLETE_PERCENTAGE * videoDuration) / 100);
  const isSetResumeTime = useRef(false);
  const watchedDuration = useRef(0);
  const videoDurationRef = useRef(videoDuration);
  videoDurationRef.current = videoDuration;
  const { activate, deactivate, playerRef, videoState, play: replay } = useMindstampVideoPlayer();
  const [isShowOverlay, setIsShowOverlay] = useState(false);

  useEffect(() => {
    if (!isSetResumeTime.current) {
      const mindstampUrl = generateMindstampUrl(
        videoUrl,
        user,
        videoProgress?.videoTime,
        isResuming
      );

      playerRef.current.src = mindstampUrl;
      isSetResumeTime.current = true;
    }
  }, [videoUrl, videoProgress]);

  useEffect(() => {
    const isVideoEnded = videoState === VIDEO_STATE.ENDED;
    if (playerRef) {
      if (isVideoEnded) {
        setIsShowOverlay(true);
      } else {
        setIsShowOverlay(false);
      }
    }
  }, [playerRef, videoState]);

  const updateVideoTime = useCallback(
    _throttle((currentTime) => {
      const videoTime = sanitizeVideoTime(currentTime, { assetDuration: videoDurationRef.current });
      if (videoTime !== null) onUpdateVideoTime(videoTime);
    }, THROTTLE_UPDATE_VIDEO_TIME),
    []
  );

  const updateClassroomTime = useCallback(
    _throttle(() => {
      if (watchedDuration.current > 0) {
        onUpdateClassroomTime(watchedDuration.current);
        watchedDuration.current = 0;
      }
    }, THROTTLE_UPDATE_CLASSROOM_TIME_INTERVAL),
    []
  );

  const mindstampListener = useCallback(
    _throttle(
      /**
       * @param {MessageEvent<import('Context/MindstampVideoPlayer/MindstampVideoPlayerProvider').MindstampData>} messageEvent
       */
      (messageEvent) => {
        // TODO: check origin and return if not mindstamp

        checkInvalidDevice();

        if (!messageEvent.data.info?.currentTime && messageEvent.data.info?.currentTime !== 0) {
          return;
        }

        const event = messageEvent.data.event;
        const currentTime = messageEvent.data.info.currentTime;

        const isSeeked = Math.abs(currentTime - previousTime.current) > 10; // assume user seek
        const shouldComplete =
          !isSetCompleted.current && videoDuration > 0 && currentTime >= completedTime.current;

        if (event === 'time_update') {
          updateVideoTime(currentTime);

          watchedDuration.current++;
          updateClassroomTime();
        }
        if (isSeeked) {
          const seekedVideoTime = sanitizeVideoTime(currentTime, {
            assetDuration: videoDurationRef.current,
          });
          if (seekedVideoTime !== null) onUpdateVideoTime(seekedVideoTime);
        }
        if (shouldComplete) {
          onCompleted();
          isSetCompleted.current = true;
        }

        previousTime.current = currentTime;
      },
      THROTTLE_VIDEO_PROGRESS_MILLISEC
    ),
    []
  );

  useEffect(() => {
    activate();
    window.addEventListener('message', mindstampListener);
    return () => {
      deactivate();
      updateVideoTime.flush();
      mindstampListener.cancel();
      window.removeEventListener('message', mindstampListener);
    };
  }, [mindstampListener, updateVideoTime]);

  return (
    <>
      {/* spacing bottom by footer icon height = 12 + 40 + 12 (pb + height + pt) = 64 */}
      <Box height="calc(100vh - 64px)" style={{ position: 'relative' }}>
        <iframe
          ref={playerRef}
          src=""
          title={title}
          allow="encrypted-media; microphone"
          sandbox="allow-scripts allow-same-origin"
          scrolling="no"
          frameBorder="0"
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            minHeight: 'unset',
            minWidth: 'unset',
            border: 0,
          }}
        ></iframe>
      </Box>
      <Box height="64px" bg="black" />
      {showVideoOverlayOnEnd &&
        renderCustomVideoOverlay({
          isShowOverlay,
          closeOverlay: () => setIsShowOverlay(false),
          replay,
        })}
    </>
  );
};

MindstampVideoBox.propTypes = {
  title: PropTypes.string,
  value: PropTypes.shape({
    provider: PropTypes.oneOf(Object.values(VIDEO_PROVIDER)),
    videoId: PropTypes.string,
    url: PropTypes.string,
    duration: PropTypes.number,
  }),
  isResuming: PropTypes.bool,
  videoProgress: PropTypes.object,
  onUpdateVideoTime: PropTypes.func,
  onCompleted: PropTypes.func,
  onUpdateClassroomTime: PropTypes.func,
  checkInvalidDevice: PropTypes.func,
  user: PropTypes.object,
  showVideoOverlayOnEnd: PropTypes.bool,
  renderCustomVideoOverlay: PropTypes.func,
};

MindstampVideoBox.defaultProps = {
  onUpdateVideoTime: () => {},
  onCompleted: () => {},
  onUpdateClassroomTime: () => {},
  checkInvalidDevice: () => {},
};

export default connect((state) => ({ user: myProfile(state) }), {})(MindstampVideoBox);

/**
 * @typedef User
 * @property {string} id
 * @property {string} additional6
 * @property {string} firstname
 */
